import React from 'react';
import { Trash2 } from 'lucide-react';
import { ContentBlock } from '../../types/content';
import { contentApi } from '../../services/contentApi';
import Button from '../UI/Button';
import Input from '../UI/Input';

const BLOCK_TYPES = [
  'Paragraph',
  'Heading',
  'Image',
  'Code',
  'Quote',
  'Callout',
  'BulletList',
  'NumberedList',
  'Table',
  'Link',
  'Video',
  'Step',
  'Warning',
  'Tip',
  'Exercise',
];

export const emptyBlock = (order: number): ContentBlock => ({
  type: 'Paragraph',
  displayOrder: order,
  content: '',
});

export const blocksFromPlainText = (text?: string | null): ContentBlock[] => {
  const value = (text || '').trim();
  if (!value) return [emptyBlock(1)];
  return [{ type: 'Paragraph', displayOrder: 1, content: value }];
};

export type MediaUploadContext = {
  courseId?: number;
  labId?: number;
  projectId?: number;
  catalogKind?: 'courses' | 'labs' | 'projects';
  entityName?: string;
  topicName?: string;
};

interface BlockEditorProps {
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
  uploadContext?: MediaUploadContext;
}

const BlockEditor: React.FC<BlockEditorProps> = ({ blocks, onChange, uploadContext }) => {
  const update = (index: number, patch: Partial<ContentBlock>) => {
    const next = [...blocks];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <div
          key={block.id ?? `block-${index}`}
          className={`rounded-xl border p-3 space-y-2 ${
            index % 2 === 0 ? 'border-gray-200 bg-white' : 'border-primary-200 bg-primary-50/70'
          }`}
        >
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="rounded-md border-gray-300"
              value={block.type}
              onChange={(e) => update(index, { type: e.target.value })}
            >
              {BLOCK_TYPES.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              className="text-error-700"
              onClick={() => onChange(blocks.filter((_, i) => i !== index))}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (index === 0) return;
                const next = [...blocks];
                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                onChange(next);
              }}
            >
              Up
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (index === blocks.length - 1) return;
                const next = [...blocks];
                [next[index + 1], next[index]] = [next[index], next[index + 1]];
                onChange(next);
              }}
            >
              Down
            </Button>
          </div>
          <textarea
            className="w-full rounded-md border-gray-300 font-mono text-sm"
            rows={block.type === 'Code' || block.type === 'Table' ? 6 : 3}
            value={block.content || ''}
            onChange={(e) => update(index, { content: e.target.value })}
            placeholder={block.type === 'BulletList' || block.type === 'NumberedList' ? 'One item per line' : 'Content'}
          />
          {block.type === 'Code' && (
            <Input
              id={`lang-${index}`}
              label="Language"
              value={block.language || ''}
              onChange={(e) => update(index, { language: e.target.value })}
            />
          )}
          {(block.type === 'Link' || block.type === 'Video') && (
            <Input
              id={`url-${index}`}
              label="URL"
              value={block.url || ''}
              onChange={(e) => update(index, { url: e.target.value })}
            />
          )}
          {block.type === 'Image' && (
            <div className="grid md:grid-cols-2 gap-2">
              <Input
                id={`alt-${index}`}
                label="Alt text"
                value={block.altText || ''}
                onChange={(e) => update(index, { altText: e.target.value })}
              />
              <Input
                id={`cap-${index}`}
                label="Caption"
                value={block.caption || ''}
                onChange={(e) => update(index, { caption: e.target.value })}
              />
              <label className="block text-sm font-medium text-gray-700 md:col-span-2">
                Image file
                <input
                  type="file"
                  accept="image/*"
                  className="mt-1 block"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const uploaded = await contentApi.uploadMedia(file, block.altText || '', block.caption || '', uploadContext);
                    update(index, { type: 'Image', mediaId: uploaded.data.media.id, media: uploaded.data.media });
                  }}
                />
              </label>
              {block.media?.url && (
                <img src={block.media.url} alt={block.altText || ''} className="h-32 rounded-lg object-cover" />
              )}
            </div>
          )}
          {block.type === 'Step' && (
            <div className="grid md:grid-cols-3 gap-2">
              <Input
                id={`exp-${index}`}
                label="Expected result"
                value={block.expectedResult || ''}
                onChange={(e) => update(index, { expectedResult: e.target.value })}
              />
              <Input
                id={`notes-${index}`}
                label="Notes"
                value={block.notes || ''}
                onChange={(e) => update(index, { notes: e.target.value })}
              />
              <Input
                id={`mistakes-${index}`}
                label="Common mistakes"
                value={block.commonMistakes || ''}
                onChange={(e) => update(index, { commonMistakes: e.target.value })}
              />
            </div>
          )}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...blocks, emptyBlock(blocks.length + 1)])}>
        Add block
      </Button>
    </div>
  );
};

export default BlockEditor;
